/**
 * AI Communication Coach - User View
 * Simplified version for regular users (not admins/auditors)
 * Shows only their own data and progress
 */

import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getCommunicationProfile,
  getCommunicationSessions,
  createCommunicationSession,
  sendCoachMessage,
  transcribeAudio,
  completeCommunicationSession,
  getCommunicationProgress,
  uploadSessionRecording,
  getUserBadges,
  getUserChallenges,
  checkBadges,
  getBadgesLeaderboard,
  getUserHomework,
  completeHomeworkTask
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Mic,
  MicOff,
  Send,
  Target,
  TrendingUp,
  Trophy,
  Clock,
  CheckCircle2,
  Star,
  Sparkles,
  Brain,
  MessageCircle,
  ChevronRight,
  Play,
  StopCircle,
  Loader2,
  BarChart3,
  Award,
  Zap,
  Medal,
  Crown,
  Gem,
  Flame,
  Shield,
  Eye,
  Rocket,
  Swords,
  Gift,
  ArrowUp,
  ArrowDown,
  ClipboardCheck,
  RefreshCw,
  Calendar,
  GraduationCap,
  BookOpen
} from 'lucide-react';
import CumulativeLearningReport from '@/components/CumulativeLearningReport';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';
import { format } from 'date-fns';

// Constants
const COACHING_PHASES = [
  { phase: 1, name: "Assessment", description: "Baseline evaluation", icon: Target, color: "bg-slate-500" },
  { phase: 2, name: "Foundation", description: "Build core skills", icon: Brain, color: "bg-blue-500" },
  { phase: 3, name: "Development", description: "Strengthen weaknesses", icon: TrendingUp, color: "bg-indigo-500" },
  { phase: 4, name: "Refinement", description: "Polish techniques", icon: Sparkles, color: "bg-purple-500" },
  { phase: 5, name: "Professional", description: "Advanced practice", icon: Star, color: "bg-amber-500" },
  { phase: 6, name: "Mastery", description: "Excellence achieved", icon: Trophy, color: "bg-emerald-500" },
];

const BADGE_ICONS = {
  rocket: Rocket, star: Star, medal: Medal, award: Award, trophy: Trophy, crown: Crown,
  "trending-up": TrendingUp, zap: Zap, sparkles: Sparkles, gem: Gem, flame: Flame, fire: Flame,
  lightning: Zap, bolt: Zap, mic: Mic, clock: Clock, eye: Eye, shield: Shield, brain: Brain,
  "arrow-up": ArrowUp, "chart-line": TrendingUp, target: Target, bullseye: Target, sword: Swords
};

export default function UserAICommCoach() {
  const { user } = useOutletContext();
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  
  // Data
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState({ earned_badges: [], progress: [] });
  const [challenges, setChallenges] = useState({ admin_challenges: [], auto_challenges: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);

  // Cumulative coaching report
  const [coachingOpen, setCoachingOpen] = useState(false);
  
  // Session state
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionTimer, setSessionTimer] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [sessionResults, setSessionResults] = useState(null);
  
  // Task submission
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskNotes, setTaskNotes] = useState('');
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);
  
  // Refs
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const fullRecordingRef = useRef([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  useEffect(() => {
    if (activeSession && !timerRef.current) {
      timerRef.current = setInterval(() => setSessionTimer(prev => prev + 1), 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeSession]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [profileRes, sessionsRes, progressRes, badgesRes, challengesRes, leaderboardRes, tasksRes] = await Promise.allSettled([
        getCommunicationProfile(),
        getCommunicationSessions(null, 20),
        getCommunicationProgress(),
        getUserBadges(),
        getUserChallenges(),
        getBadgesLeaderboard(),
        getUserHomework()
      ]);
      
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);
      if (sessionsRes.status === 'fulfilled') setSessions(sessionsRes.value.data.sessions || []);
      if (progressRes.status === 'fulfilled') setProgress(progressRes.value.data);
      if (badgesRes.status === 'fulfilled') setBadges(badgesRes.value.data || { earned_badges: [], progress: [] });
      if (challengesRes.status === 'fulfilled') setChallenges(challengesRes.value.data || { admin_challenges: [], auto_challenges: [] });
      if (leaderboardRes.status === 'fulfilled') setLeaderboard(leaderboardRes.value.data?.leaderboard || []);
      if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value.data?.tasks || []);
      
      try {
        const checkRes = await checkBadges();
        if (checkRes.data?.new_badges?.length > 0) {
          setNewBadges(checkRes.data.new_badges);
          setShowBadgeDialog(true);
        }
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startSession = async (type = 'practice') => {
    setIsSessionLoading(true);
    try {
      const res = await createCommunicationSession(type);
      setActiveSession(res.data.session);
      setMessages([{ role: 'assistant', content: res.data.initial_message }]);
      setSessionTimer(0);
      fullRecordingRef.current = [];
      toast.success(`${type === 'assessment' ? 'Assessment' : 'Practice'} started!`);
    } catch (error) {
      toast.error('Failed to start session');
    } finally {
      setIsSessionLoading(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim() || !activeSession) return;
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setInputMessage('');
    setIsSessionLoading(true);
    try {
      const res = await sendCoachMessage(activeSession.id, message, activeSession.session_type === 'assessment');
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Failed to get response');
    } finally {
      setIsSessionLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          fullRecordingRef.current.push(e.data);
        }
      };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        setIsTranscribing(true);
        try {
          const res = await transcribeAudio(audioBlob);
          if (res.data.text?.trim()) sendMessage(res.data.text);
        } catch (error) {
          toast.error('Transcription failed');
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleEndSession = async () => {
    setShowEndSessionDialog(false);
    setIsSessionLoading(true);
    try {
      if (fullRecordingRef.current.length > 0) {
        const fullRecording = new Blob(fullRecordingRef.current, { type: 'audio/webm' });
        await uploadSessionRecording(activeSession.id, fullRecording).catch(() => {});
      }
      const res = await completeCommunicationSession(activeSession.id, currentTranscript);
      setSessionResults(res.data.assessment);
      setShowResultsDialog(true);
      setActiveSession(null);
      setMessages([]);
      setSessionTimer(0);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      loadData();
    } catch (error) {
      toast.error('Failed to complete session');
    } finally {
      setIsSessionLoading(false);
    }
  };

  const handleCompleteTask = async () => {
    if (!selectedTask || !taskNotes.trim()) {
      toast.error('Please provide completion notes');
      return;
    }
    setIsSubmittingTask(true);
    try {
      await completeHomeworkTask(selectedTask.id, { completion_notes: taskNotes });
      toast.success('Task completed!');
      setSelectedTask(null);
      setTaskNotes('');
      loadData();
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setIsSubmittingTask(false);
    }
  };

  const getSkillRadarData = () => {
    const skills = profile?.skill_scores || {};
    return [
      { skill: 'Pronunciation', value: skills.pronunciation || 0 },
      { skill: 'Pace', value: skills.pace || 0 },
      { skill: 'Clarity', value: skills.clarity || 0 },
      { skill: 'Confidence', value: skills.confidence || 0 },
      { skill: 'Comprehension', value: skills.comprehension || 0 },
    ];
  };

  const getProgressChartData = () => {
    return (profile?.improvement_trend || []).map((score, i) => ({ session: i + 1, score }));
  };

  const getCurrentPhase = () => {
    const num = progress?.current_phase || profile?.coaching_phase || 1;
    return COACHING_PHASES.find(p => p.phase === num) || COACHING_PHASES[0];
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[status] || 'bg-muted text-muted-foreground';
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="user-coach-loading">
        <Skeleton className="h-24" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const currentPhase = getCurrentPhase();
  const currentScore = profile?.overall_score || 0;
  const totalSessions = sessions.length;
  const earnedBadges = badges?.earned_badges?.length || 0;
  const pendingTasks = tasks.filter(t => t.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="user-ai-comm-coach">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-xl text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <MessageCircle className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">AI Communication Coach</h1>
            <p className="text-purple-100">Your personal communication trainer</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-center bg-white/10 rounded-xl px-4 py-2">
            <p className="text-2xl font-bold">{Math.round(currentScore)}</p>
            <p className="text-xs text-purple-200">Score</p>
          </div>
          <div className="text-center bg-white/10 rounded-xl px-4 py-2">
            <p className="text-2xl font-bold">{progress?.current_streak || 0}</p>
            <p className="text-xs text-purple-200">Streak</p>
          </div>
          <Button variant="secondary" size="sm" onClick={loadData} className="bg-white/20 hover:bg-white/30 text-white border-0">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 h-12">
          <TabsTrigger value="dashboard" className="gap-2" data-testid="user-tab-dashboard">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="practice" className="gap-2" data-testid="user-tab-practice">
            <Mic className="h-4 w-4" />
            <span className="hidden sm:inline">Practice</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2" data-testid="user-tab-tasks">
            <ClipboardCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Tasks</span>
            {pendingTasks > 0 && <Badge variant="destructive" className="ml-1">{pendingTasks}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="achievements" className="gap-2" data-testid="user-tab-achievements">
            <Trophy className="h-4 w-4" />
            <span className="hidden sm:inline">Achievements</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* AI Coaching Report CTA — self-service */}
          <Card
            data-testid="user-cumulative-coaching-cta"
            className="border-2 border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/30 dark:from-violet-950/20 dark:to-fuchsia-950/10"
          >
            <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md shrink-0">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    Your AI Coaching Report
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Recurring strengths, common misses with rephrased scripts, and your personalised 4-week practice plan — synthesised from every call you've made.
                  </p>
                </div>
              </div>
              <Button
                data-testid="user-cumulative-coaching-open"
                onClick={() => setCoachingOpen(true)}
                className="gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shrink-0"
              >
                <Sparkles className="h-4 w-4" />
                View report
              </Button>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-violet-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Target className="h-8 w-8 text-violet-500" />
                  <div>
                    <p className="text-2xl font-bold">{Math.round(currentScore)}</p>
                    <p className="text-xs text-muted-foreground">Overall Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <MessageCircle className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{totalSessions}</p>
                    <p className="text-xs text-muted-foreground">Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Award className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="text-2xl font-bold">{earnedBadges}/25</p>
                    <p className="text-xs text-muted-foreground">Badges</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Flame className="h-8 w-8 text-emerald-500" />
                  <div>
                    <p className="text-2xl font-bold">{progress?.current_streak || 0}</p>
                    <p className="text-xs text-muted-foreground">Day Streak</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Journey Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Your Journey
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                {COACHING_PHASES.map((phase) => {
                  const PhaseIcon = phase.icon;
                  const isActive = currentPhase.phase === phase.phase;
                  const isCompleted = currentPhase.phase > phase.phase;
                  return (
                    <div key={phase.phase} className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all
                        ${isActive ? `${phase.color} text-white ring-2 ring-offset-2` : ''}
                        ${isCompleted ? 'bg-emerald-500 text-white' : ''}
                        ${!isActive && !isCompleted ? 'bg-muted text-muted-foreground' : ''}
                      `}>
                        {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <PhaseIcon className="h-5 w-5" />}
                      </div>
                      <p className={`text-xs text-center ${isActive ? 'font-semibold' : 'text-muted-foreground'}`}>{phase.name}</p>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">Phase {currentPhase.phase}: {currentPhase.name}</p>
                <p className="text-sm text-muted-foreground">{currentPhase.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Skill Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={getSkillRadarData()}>
                      <PolarGrid stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <PolarAngleAxis dataKey="skill" tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                      <Radar name="Score" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.5} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Progress Over Time</CardTitle></CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={getProgressChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="session" tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }} />
                      <YAxis domain={[0, 100]} tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#fff' }} />
                      <Area type="monotone" dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="cursor-pointer hover:border-amber-500 transition-colors" onClick={() => { setActiveTab('practice'); startSession('assessment'); }}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                  <Target className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Take Assessment</h3>
                  <p className="text-sm text-muted-foreground">Evaluate your current skill level</p>
                </div>
                <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-blue-500 transition-colors" onClick={() => { setActiveTab('practice'); startSession('practice'); }}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <MessageCircle className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Start Practice</h3>
                  <p className="text-sm text-muted-foreground">Practice with AI coach</p>
                </div>
                <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Practice Tab */}
        <TabsContent value="practice" className="space-y-6">
          {!activeSession ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-2 border-dashed border-amber-300 hover:border-amber-500 cursor-pointer" onClick={() => startSession('assessment')}>
                  <CardContent className="p-8 text-center">
                    <Target className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Take Assessment</h3>
                    <p className="text-muted-foreground mb-4">Get baseline evaluation of your skills</p>
                    <Button className="bg-amber-500 hover:bg-amber-600" disabled={isSessionLoading}>
                      {isSessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Target className="h-4 w-4 mr-2" />}
                      Start Assessment
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-blue-300 hover:border-blue-500 cursor-pointer" onClick={() => startSession('practice')}>
                  <CardContent className="p-8 text-center">
                    <MessageCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Practice Session</h3>
                    <p className="text-muted-foreground mb-4">Engage in conversation practice</p>
                    <Button className="bg-blue-500 hover:bg-blue-600" disabled={isSessionLoading}>
                      {isSessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                      Start Practice
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Sessions */}
              <Card>
                <CardHeader><CardTitle>Recent Sessions</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sessions.slice(0, 5).map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-3">
                          {session.session_type === 'assessment' ? <Target className="h-5 w-5 text-amber-500" /> : <MessageCircle className="h-5 w-5 text-blue-500" />}
                          <div>
                            <p className="font-medium capitalize">{session.session_type}</p>
                            <p className="text-xs text-muted-foreground">{session.created_at ? format(new Date(session.created_at), 'MMM d, h:mm a') : '-'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">{Math.round(session.overall_score || 0)}</p>
                        </div>
                      </div>
                    ))}
                    {sessions.length === 0 && <p className="text-center py-8 text-muted-foreground">No sessions yet. Start your first one above!</p>}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="h-[calc(100vh-300px)] flex flex-col">
              <CardHeader className="flex-none border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant={activeSession.session_type === 'assessment' ? 'default' : 'secondary'}>{activeSession.session_type}</Badge>
                    <Clock className="h-4 w-4" />
                    <span className="font-mono">{formatTime(sessionTimer)}</span>
                  </div>
                  <Button variant="destructive" size="sm" onClick={() => setShowEndSessionDialog(true)}>
                    <StopCircle className="h-4 w-4 mr-2" /> End
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-violet-500 text-white rounded-br-none' : 'bg-muted rounded-bl-none'}`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {isSessionLoading && <div className="flex justify-start"><div className="bg-muted p-4 rounded-2xl rounded-bl-none"><Loader2 className="h-5 w-5 animate-spin" /></div></div>}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
              <div className="flex-none p-4 border-t">
                <div className="flex items-center gap-2">
                  <Button variant={isRecording ? 'destructive' : 'outline'} size="icon" onClick={isRecording ? stopRecording : startRecording} disabled={isTranscribing}>
                    {isTranscribing ? <Loader2 className="h-5 w-5 animate-spin" /> : isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </Button>
                  <Input placeholder="Type or speak..." value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage(inputMessage)} disabled={isSessionLoading} className="flex-1" />
                  <Button onClick={() => sendMessage(inputMessage)} disabled={!inputMessage.trim() || isSessionLoading}><Send className="h-5 w-5" /></Button>
                </div>
                {isRecording && <p className="text-center text-sm text-red-500 mt-2 animate-pulse">Recording...</p>}
              </div>
              <audio ref={audioRef} className="hidden" />
            </Card>
          )}
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{tasks.filter(t => t.status === 'pending').length}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{tasks.filter(t => t.status === 'in_progress').length}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{tasks.filter(t => t.status === 'completed').length}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Your Weekly Tasks
              </CardTitle>
              <CardDescription>Complete tasks to improve your skills and earn badges</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tasks.map((task) => (
                  <div key={task.id} className={`p-4 rounded-lg border ${task.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200' : task.status === 'overdue' ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : 'bg-muted'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{task.title}</h4>
                          <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {task.skill_focus && <span className="flex items-center gap-1"><Brain className="h-3 w-3" /> {task.skill_focus}</span>}
                          {task.deadline && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(task.deadline), 'MMM d')}</span>}
                        </div>
                      </div>
                      {task.status !== 'completed' && (
                        <Button size="sm" onClick={() => setSelectedTask(task)}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No tasks assigned yet.</p>
                    <p className="text-sm">Complete sessions to receive personalized tasks!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" />
                Your Badges
                <Badge variant="secondary">{earnedBadges}/25</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {badges?.earned_badges?.map((badge) => {
                  const Icon = BADGE_ICONS[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="p-3 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-xl text-center border border-amber-200">
                      <div className="p-2 bg-amber-500 rounded-full w-fit mx-auto mb-2"><Icon className="h-5 w-5 text-white" /></div>
                      <p className="font-medium text-xs">{badge.name}</p>
                    </div>
                  );
                })}
                {badges?.progress?.filter(p => !p.earned).slice(0, 5).map((badge) => {
                  const Icon = BADGE_ICONS[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="p-3 bg-muted rounded-xl text-center border border-dashed opacity-60">
                      <div className="p-2 bg-muted-foreground/20 rounded-full w-fit mx-auto mb-2"><Icon className="h-5 w-5" /></div>
                      <p className="font-medium text-xs text-muted-foreground">{badge.name}</p>
                      <Progress value={badge.progress_percent || 0} className="h-1 mt-1" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-purple-500" /> Challenges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...(challenges?.auto_challenges || []), ...(challenges?.admin_challenges || [])].map((ch) => (
                  <div key={ch.id} className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{ch.title}</h4>
                      <Badge variant={ch.status === 'completed' ? 'success' : 'secondary'}>{ch.status}</Badge>
                    </div>
                    <Progress value={(ch.progress / ch.target_value) * 100} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{ch.progress}/{ch.target_value}</p>
                  </div>
                ))}
                {(!challenges?.auto_challenges?.length && !challenges?.admin_challenges?.length) && (
                  <p className="text-center py-8 text-muted-foreground">No active challenges</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Leaderboard</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((entry, idx) => (
                  <div key={entry.user_id} className={`flex items-center justify-between p-2 rounded-lg ${entry.user_id === user?.id ? 'bg-violet-100 dark:bg-violet-900/30 border border-violet-300' : idx < 3 ? 'bg-muted' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-amber-500 text-white' : idx === 1 ? 'bg-slate-400 text-white' : idx === 2 ? 'bg-orange-500 text-white' : 'bg-muted'}`}>{idx + 1}</span>
                      <Avatar className="h-6 w-6"><AvatarFallback className="text-xs">{entry.user_name?.charAt(0)}</AvatarFallback></Avatar>
                      <span className="text-sm font-medium">{entry.user_name} {entry.user_id === user?.id && '(You)'}</span>
                    </div>
                    <div className="flex items-center gap-1"><Award className="h-4 w-4 text-amber-500" /><span className="font-bold">{entry.badge_count}</span></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={showEndSessionDialog} onOpenChange={setShowEndSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Session?</DialogTitle>
            <DialogDescription>Your conversation will be analyzed and scored.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndSessionDialog(false)}>Cancel</Button>
            <Button onClick={handleEndSession} disabled={isSessionLoading}>{isSessionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}End Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Session Complete!</DialogTitle></DialogHeader>
          {sessionResults && (
            <div className="space-y-4">
              <div className="text-center p-6 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 rounded-xl">
                <p className="text-5xl font-bold text-violet-600">{Math.round(sessionResults.overall_score || 0)}</p>
                <p className="text-muted-foreground">Overall Score</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(sessionResults.skill_scores || {}).map(([skill, score]) => (
                  <div key={skill} className="p-2 bg-muted rounded text-center">
                    <p className="text-xs text-muted-foreground capitalize">{skill}</p>
                    <p className="font-bold">{Math.round(score)}</p>
                  </div>
                ))}
              </div>
              {sessionResults.feedback && <div className="p-3 bg-muted rounded"><p className="text-sm">{sessionResults.feedback}</p></div>}
            </div>
          )}
          <DialogFooter><Button onClick={() => setShowResultsDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 justify-center"><Gift className="h-6 w-6 text-amber-500" /> New Badge!</DialogTitle></DialogHeader>
          {newBadges.map((badge) => {
            const Icon = BADGE_ICONS[badge.icon] || Award;
            return (
              <div key={badge.id} className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 rounded-xl">
                <div className="p-4 bg-amber-500 rounded-full w-fit mx-auto mb-3"><Icon className="h-10 w-10 text-white" /></div>
                <p className="text-xl font-bold">{badge.name}</p>
                <p className="text-muted-foreground">{badge.description}</p>
              </div>
            );
          })}
          <DialogFooter><Button onClick={() => setShowBadgeDialog(false)} className="w-full">Awesome!</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
            <DialogDescription>{selectedTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{selectedTask?.description}</p>
            <Textarea placeholder="Describe how you completed this task..." value={taskNotes} onChange={(e) => setTaskNotes(e.target.value)} rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTask(null)}>Cancel</Button>
            <Button onClick={handleCompleteTask} disabled={isSubmittingTask || !taskNotes.trim()}>
              {isSubmittingTask && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cumulative coaching report — self-service for the recruiter */}
      <CumulativeLearningReport
        userId={user?.id}
        userName={user?.name}
        open={coachingOpen}
        onOpenChange={setCoachingOpen}
        canRegenerate={true}
      />
    </div>
  );
}
